import React, { useState, useEffect } from 'react';
import api from '../api';
import { 
  FaUserTie, FaCertificate, FaMapMarkerAlt, FaThermometerHalf, 
  FaTools, FaChartLine, FaLeaf, FaSolarPanel, FaCheckCircle, 
  FaTree, FaCar, FaBolt, FaCloud 
} from 'react-icons/fa';

const Benefits = () => {
  const [benefits, setBenefits] = useState([]);
  const [impactStats, setImpactStats] = useState([]);

  // --- ICON MAP (Maps DB String -> React Component) ---
  const iconMap = {
    "FaUserTie": <FaUserTie />,
    "FaCertificate": <FaCertificate />,
    "FaMapMarkerAlt": <FaMapMarkerAlt />,
    "FaThermometerHalf": <FaThermometerHalf />,
    "FaTools": <FaTools />,
    "FaChartLine": <FaChartLine />,
    "FaLeaf": <FaLeaf />,
    "FaSolarPanel": <FaSolarPanel />,
    "FaCheckCircle": <FaCheckCircle />,
    "FaTree": <FaTree />,
    "FaCar": <FaCar />,
    "FaBolt": <FaBolt />,
    "FaCloud": <FaCloud />
  };


  useEffect(() => {
    const fetchData = async () => {
      try {
      const [benefitsRes, impactRes] = await Promise.all([
  api.get('/Benefits/list'),   // Base URL auto-handle hoga
  api.get('/Benefits/impact')
]);

        // 1. Set Benefits
        if (benefitsRes.data && benefitsRes.data.length > 0) {
          setBenefits(benefitsRes.data);
        } else {
          setBenefits([
            { iconName: "FaUserTie", title: "Engineer-Driven", description: "Designed by NIT & DTU engineers." },
            { iconName: "FaCertificate", title: "Govt Approved", description: "Empanelled with SBPDCL & NBPDCL." }
          ]);
        }

        // 2. Set Impact Stats (Update Fallback to use Icon Names, NOT Emojis)
        if (impactRes.data && impactRes.data.length > 0) {
          setImpactStats(impactRes.data);
        } else {
          setImpactStats([
            { iconEmoji: "FaBolt", value: "37,500", unit: "kWh", label: "Energy Generated", description: "Clean power produced", colorHex: "#FFD700" },
            { iconEmoji: "FaTree", value: "120", unit: "Trees", label: "Trees Planted", description: "Equivalent impact", colorHex: "#2E7D32" }
          ]);
        }

      } catch (err) {
        console.error("Error fetching benefits data:", err);
      }
    };

    fetchData();
  }, []);

  return (
    <section className="benefits py-5 bg-light" id="why-us">
      <div className="container">
        
        {/* Why Choose Section Header */}
        <div className="text-center mb-5">
          <span className="badge bg-success mb-3 px-3 py-2">Why Choose RENGIE</span>
          <h2 className="section-title display-5 fw-bold text-dark mb-3">
            What Makes Us Different
          </h2>
          <p className="section-subtitle lead text-muted">
            Engineering excellence, transparency, and long-term commitment
          </p>
        </div>

        {/* Benefits Grid (Dynamic) */}
        <div className="row g-4 mb-5">
          {benefits.map((benefit, index) => (
            <div className="col-lg-4 col-md-6" key={index}>
              <div 
                className="card border-0 shadow-sm h-100"
                style={{ transition: 'all 0.3s ease', cursor: 'pointer' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px)';
                  e.currentTarget.style.boxShadow = '0 15px 40px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                }}
              >
                <div className="card-body p-4 text-center">
                  <div className="benefit-icon mb-3" style={{ fontSize: '3rem', color: '#00D084' }}>
                    {/* Render Icon from Map */}
                    {iconMap[benefit.iconName] || <FaCheckCircle />}
                  </div>
                  <h5 className="fw-bold mb-3" style={{ color: '#1B3F6E' }}>
                    {benefit.title}
                  </h5>
                  <p className="text-muted mb-0" style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>
                    {benefit.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Environmental Impact Section (Dynamic) */}
        <div className="environmental-impact-section mt-5 pt-5" style={{ borderTop: '3px solid #E0E0E0' }}>
          <div className="text-center mb-5">
            <span className="badge bg-success mb-3 px-3 py-2">
              <FaLeaf className="me-2" /> Environmental Impact
            </span>
            <h2 className="display-5 fw-bold text-dark mb-3">
              Your Solar Impact on Planet Earth
            </h2>
            <p className="lead text-muted mb-2">
              Every 1 kW solar system you install makes a real difference
            </p>
            <p className="text-muted">
              Here's the environmental benefit your solar system provides over 25 years
            </p>
          </div>

          {/* Impact Cards */}
          <div className="row g-4 mb-5">
            {impactStats.map((impact, index) => (
              <div className="col-lg-3 col-md-6" key={index}>
                <div 
                  className="card border-0 shadow-lg h-100 text-center p-4"
                  style={{
                    transition: 'all 0.3s ease',
                    background: `linear-gradient(135deg, ${impact.colorHex}15 0%, ${impact.colorHex}08 100%)`,
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-15px)';
                    e.currentTarget.style.boxShadow = '0 20px 50px rgba(0, 0, 0, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.1)';
                  }}
                >
                  {/* Icon Wrapper */}
                  <div 
                    className="impact-icon mx-auto mb-3"
                    style={{
                      width: '100px', height: '100px',
                      background: impact.colorHex,
                      borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '3rem',
                      color: '#ffffff', // Icon white color
                      boxShadow: `0 10px 30px ${impact.colorHex}50`,
                      transition: 'transform 0.3s ease'
                    }}
                  >
                    {/* 👇 UPDATE: Use IconMap instead of printing text */}
                    {iconMap[impact.iconEmoji] || <FaLeaf />}
                  </div>

                  <h2 className="fw-bold mb-1" style={{ color: impact.colorHex, fontSize: '3rem' }}>
                    {impact.value}
                  </h2>
                  <p className="text-muted fw-semibold mb-3" style={{ fontSize: '1.1rem' }}>
                    {impact.unit}
                  </p>
                  <h5 className="fw-bold mb-2" style={{ color: '#1B3F6E', fontSize: '1.1rem' }}>
                    {impact.label}
                  </h5>
                  <p className="text-muted small mb-0">
                    {impact.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Info Box (Static) */}
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <div 
                className="card border-0 shadow-lg"
                style={{
                  background: 'linear-gradient(135deg, #E8F8F3 0%, #E3F2FD 100%)',
                  border: '3px solid #00D084'
                }}
              >
                <div className="card-body p-4 d-flex align-items-center">
                  <div className="me-4 d-none d-md-block">
                    {/* Direct icon usage for static part is fine */}
                    <FaCloud style={{ fontSize: '5rem', color: '#00D084' }} />
                  </div>
                  <div>
                    <h5 className="fw-bold mb-3" style={{ color: '#1B3F6E' }}>
                      <FaLeaf className="me-2" style={{ color: '#4CAF50' }} />
                      Make a Real Difference for Future Generations
                    </h5>
                    <p className="mb-0 text-muted" style={{ lineHeight: '1.7' }}>
                      By choosing solar, you're not just saving money on electricity bills—you're actively 
                      contributing to combat climate change and building a sustainable future. Join thousands 
                      of households across <strong>Bihar & Jharkhand</strong> making this positive environmental 
                      impact while enjoying <strong>85-95% bill reduction</strong>.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Call to Action (Static) */}
          <div className="text-center mt-5">
            <a 
              href="#contact" 
              className="btn btn-lg rounded-pill px-5 py-3 shadow-lg"
              style={{
                background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
                color: '#ffffff', border: 'none', fontSize: '1.1rem', transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 15px 40px rgba(76, 175, 80, 0.4)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)'; }}
            >
              <FaLeaf className="me-2" /> Start Your Green Energy Journey Today
            </a>
            <p className="text-muted small mt-3 mb-0">
              Free site survey | Expert guidance | Government subsidy support
            </p>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Benefits;